/**
 * Families, and what each one expects an article to carry.
 *
 * `required_attributes` is the only field on this screen that does anything
 * beyond labelling: it is what the article list's completion meter measures
 * against. A family that requires nothing reports every article as complete,
 * which is correct — reporting 0% would send the ADV chasing work that does
 * not exist.
 *
 * The delivered extract's families are dirty in ways this screen has to make
 * visible rather than hide: `CARROSSERIE` and `CARROSSERIE1` both exist,
 * `Lubrifiant` appears in four spellings, and `NULL` is a real code carried by
 * 1 884 articles. Showing the article count beside each is what lets somebody
 * see that two of them are the same family split in half.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import {
  Button,
  DataTable,
  Dialog,
  Input,
  Page,
  Select,
  StatusDot,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import { ReferentialApi, type Family } from "../api";
import { NAMESPACE } from "../i18n";
import { DeleteDialog, type DeleteTarget } from "./structure/DeleteDialog";
import "./referential.css";

export function FamiliesScreen({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new ReferentialApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayWrite = useCan("referential.family.write");

  const fetcher = useCallback(() => api.listFamilies(), [api]);
  const families = useResource(fetcher);

  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [merging, setMerging] = useState<Family | null>(null);
  const [into, setInto] = useState("");
  const [deleting, setDeleting] = useState<DeleteTarget | null>(null);

  function askDelete(row: Family) {
    setDeleting({
      title: t("deletion.title.family", { name: row.label, code: row.code }),
      consequences: [
        ...(row.article_count > 0
          ? [t("deletion.will.detached", { count: row.article_count })]
          : []),
        t("deletion.will.familyRules"),
      ],
      run: () => api.deleteFamily(row.code),
    });
  }

  const columns: Column<Family>[] = [
    { key: "code", header: t("families.column.code"), render: (row) => <code>{row.code}</code> },
    { key: "label", header: t("families.column.label"), render: (row) => row.label },
    {
      key: "articles",
      header: t("families.column.articles"),
      render: (row) => <span className="hardauto-num">{row.article_count}</span>,
    },
    {
      key: "required",
      header: t("families.column.required"),
      render: (row) =>
        row.required_attributes.length === 0 ? (
          <span className="hardauto-cell__muted">{t("families.none")}</span>
        ) : (
          <span className="hardauto-cell__muted">{row.required_attributes.join(", ")}</span>
        ),
    },
    { key: "unit", header: t("families.column.unit"), render: (row) => row.default_unit },
    {
      key: "state",
      header: t("families.column.state"),
      render: (row) =>
        // A merged family is inactive, and saying only "inactive" leaves the
        // reader wondering why — and inviting them to reactivate it, which
        // would undo the merge on the next import.
        row.merged_into ? (
          <span className="hardauto-cell__muted">
            {t("families.mergedInto", { code: row.merged_into })}
          </span>
        ) : (
          <StatusDot
            label={row.is_active ? t("families.state.active") : t("families.state.inactive")}
            tone={row.is_active ? "success" : "neutral"}
          />
        ),
    },
    {
      key: "merge",
      header: t("families.column.actions"),
      align: "end",
      render: (row) =>
        // Offered on a family that is still real: merging a redirection is
        // refused by the server, and a button that only fails is a button
        // that teaches people to distrust the screen.
        mayWrite ? (
          <span className="hardauto-structure__inline hardauto-structure__inline--end">
            {!row.merged_into && row.is_active ? (
              <Button size="sm" onClick={() => setMerging(row)}>
                {t("families.merge")}
              </Button>
            ) : null}
            {/* Offered on every family, a redirection included: deleting
                one is how a merge is undone for good. */}
            <Button size="sm" variant="ghost" onClick={() => askDelete(row)}>
              {t("deletion.action")}
            </Button>
          </span>
        ) : null,
    },
  ];

  /** The families a merge may land on: real ones, minus the source itself. */
  const targets = (families.data?.items ?? []).filter(
    (entry) => entry.is_active && !entry.merged_into && entry.code !== merging?.code,
  );

  return (
    <Page
      title={t("families.title")}
      description={t("families.intro")}
      actions={
        mayWrite && !creating ? (
          <Button variant="primary" onClick={() => setCreating(true)}>
            {t("families.create")}
          </Button>
        ) : undefined
      }
    >
      <DeleteDialog
        target={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={families.reload}
      />

      {creating ? (
        <form
          className="hardauto-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!code.trim()) return;
            void action.run("create", async () => {
              try {
                await api.createFamily({ code: code.trim(), label: label.trim() });
                setCode("");
                setLabel("");
                setCreating(false);
                families.reload();
              } catch {
                toast.show(t("error.title"), { tone: "danger" });
              }
            });
          }}
        >
          <div className="hardauto-inline-form__field">
            <Field label={t("families.newCode")} required>
              {(props) => (
                <Input {...props} value={code} onChange={(event) => setCode(event.target.value)} />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <Field label={t("families.newLabel")}>
              {(props) => (
                <Input
                  {...props}
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__actions">
            <Button
              type="submit"
              variant="primary"
              busy={action.isBusy("create")}
              busyLabel={t("families.save")}
            >
              {t("families.save")}
            </Button>
            <Button onClick={() => setCreating(false)}>{t("families.cancel")}</Button>
          </div>
        </form>
      ) : null}

      <DataTable<Family>
        caption={t("families.caption")}
        columns={columns}
        rows={families.data?.items ?? []}
        rowKey={(row) => row.code}
        loading={families.loading}
        loadingLabel={t("families.loading")}
        error={
          families.failed
            ? { title: t("error.title"), onRetry: families.reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={{ title: t("families.empty.title"), description: t("families.empty.body") }}
      />

      <Dialog
        open={merging !== null}
        onClose={() => {
          setMerging(null);
          setInto("");
        }}
        title={t("families.mergeTitle", { code: merging?.code ?? "" })}
        description={t("families.mergeBody", { count: merging?.article_count ?? 0 })}
        closeLabel={t("families.cancel")}
        footer={
          <>
            <Button
              onClick={() => {
                setMerging(null);
                setInto("");
              }}
            >
              {t("families.cancel")}
            </Button>
            <Button
              variant="primary"
              busy={action.isBusy("merge")}
              busyLabel={t("families.mergeConfirm")}
              disabled={!into}
              onClick={() =>
                void action.run("merge", async () => {
                  if (!merging || !into) return;
                  try {
                    const outcome = await api.mergeFamily(merging.code, into);
                    // The count, not "done": it is the only thing that says
                    // the family merged was the family meant.
                    toast.show(
                      t("families.merged", {
                        count: outcome.articles_moved,
                        source: outcome.source,
                        target: outcome.target,
                      }),
                      { tone: "success" },
                    );
                    setMerging(null);
                    setInto("");
                    families.reload();
                  } catch {
                    toast.show(t("families.mergeFailed"), { tone: "danger" });
                  }
                })
              }
            >
              {t("families.mergeConfirm")}
            </Button>
          </>
        }
      >
        <div className="hardauto-form">
          <Field label={t("families.mergeInto")} hint={t("families.mergeHint")} required>
            {(props) => (
              <Select {...props} value={into} onChange={(event) => setInto(event.target.value)}>
                <option value="">—</option>
                {targets.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.code} — {entry.label} ({entry.article_count})
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Dialog>
    </Page>
  );
}
